import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import { uniqBy } from "lodash";
import { SESSION_KEY } from "constants/key";
import { EstQuoteDetailTire, ITireItemDetail } from "features/car_model/types";
import { getPructureProtectionApi } from "features/mst_option/api/optionApi";
import { PunctureProtection } from "features/mst_option/types/option";
import {
  CategoryListItem,
  ITireInfo,
  MstItemDetailTireSelect,
  MstProcFeeTireItem,
  SelectedTireProcFeeParams,
} from "features/mst_proc_fee/types/proc-fee";
import { getItemDetailTireSetApi } from "features/mst_proc_fee/api/procFeeApi";
import { TireResponse } from "features/car_model/api/carModelApi";
import { TOption } from "type";
import { fetchAndMapInventory } from "features/mst_proc_fee/utils/inventoryUtils";
import {
  buildProcFeeFilterQueryParams,
  useProcFeeFilter,
  type ProcFeeFilterSelection,
} from "features/mst_proc_fee/hooks/useProcFeeFilter";

const PER_PAGE_DEFAULT = 20;

// ============================================================================
// Types
// ============================================================================

interface TireState {
  tireList: ITireItemDetail[];
  originTireMakerList: ITireItemDetail[];
  makerList: TOption[];
  selectedMaker: boolean[];
  categoryList: CategoryListItem[];
  selectedCategory: boolean[];
  // Keyed by String(tire.id) — the server-assigned numeric PK, which is
  // guaranteed unique even when item_cd has duplicates across pages.
  selectedTireIds: Record<string, boolean>;
  totalSelectedTire: number;
  procFees: MstProcFeeTireItem | null;
  punctureProtection: PunctureProtection[];
  tires: ITireInfo[];
  page: number;
  total: number;
  recommendFlag: boolean;
  loading: boolean;
  // Response-level flag from list.store.price_setting_wheel. Drives whether
  // /order/bo/cost/fetch runs and whether calculator takes the special-pricing branch.
  priceSettingWheel: number;
}

type TireAction =
  | { type: "SET_TIRE_LIST"; payload: ITireItemDetail[] }
  | { type: "APPEND_TIRE_LIST"; payload: ITireItemDetail[] }
  | { type: "SET_ORIGIN_TIRE_LIST"; payload: ITireItemDetail[] }
  | { type: "APPEND_ORIGIN_TIRE_LIST"; payload: ITireItemDetail[] }
  | { type: "SET_MAKER_LIST"; payload: TOption[] }
  | { type: "SET_SELECTED_MAKER"; payload: boolean[] }
  | { type: "SET_CATEGORY_LIST"; payload: CategoryListItem[] }
  | { type: "SET_SELECTED_CATEGORY"; payload: boolean[] }
  | { type: "SET_SELECTED_TIRE_IDS"; payload: Record<string, boolean> }
  // Merges new page entries into the existing map without overwriting user selections.
  | { type: "MERGE_SELECTED_TIRE_IDS"; payload: Record<string, boolean> }
  | { type: "UPDATE_SELECTED_TIRE_IDS"; payload: { tireId: string; selected: boolean } }
  | { type: "RESET_SELECTED_TIRE_IDS" }
  | { type: "SET_TOTAL_SELECTED_TIRE"; payload: number }
  | { type: "ADD_TOTAL_SELECTED_TIRE"; payload: number }
  | { type: "INCREMENT_TOTAL_SELECTED_TIRE" }
  | { type: "SET_PROC_FEES"; payload: MstProcFeeTireItem }
  | { type: "UPDATE_PROC_FEES"; payload: Partial<MstProcFeeTireItem> }
  | { type: "SET_PUNCTURE_PROTECTION"; payload: PunctureProtection[] }
  | { type: "SET_TIRES"; payload: ITireInfo[] }
  | { type: "SET_PAGE"; payload: number }
  | { type: "INCREMENT_PAGE" }
  | { type: "SET_TOTAL"; payload: number }
  | { type: "SET_RECOMMEND_FLAG"; payload: boolean }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_PRICE_SETTING_WHEEL"; payload: number }
  | { type: "RESET_FILTER" };

interface SessionStorageData {
  previousPage: string;
  tireCategory: string | null;
  mstProcFee: any;
  currentPage: number;
  selectedMaker: TOption[];
  selectedCategory: CategoryListItem[];
  storageTire: ITireInfo[];
  recommendFlag: boolean;
}

// ============================================================================
// Reducer
// ============================================================================

const initialState: TireState = {
  tireList: [],
  originTireMakerList: [],
  makerList: [],
  selectedMaker: [],
  categoryList: [],
  selectedCategory: [],
  selectedTireIds: {},
  totalSelectedTire: 0,
  procFees: null,
  punctureProtection: [],
  tires: [],
  page: 1,
  total: 0,
  recommendFlag: false,
  loading: false,
  priceSettingWheel: 0,
};

/**
 * Normalize proc fee values by converting null to 0
 * @param procFees - Proc fee data that may contain null values
 * @returns Normalized proc fee data with null values replaced by 0
 */
function normalizeProcFees(procFees: MstProcFeeTireItem | null): MstProcFeeTireItem | null {
  if (!procFees) {
    return procFees;
  }

  return {
    replac_labor_cost: procFees.replac_labor_cost ?? 0,
    waste_tire_cost: procFees.waste_tire_cost ?? 0,
    rubber_stem_valve_cost: procFees.rubber_stem_valve_cost ?? 0,
    nitrogen_inflation_cost: procFees.nitrogen_inflation_cost ?? 0,
    hub_rust_prevention_cost: procFees.hub_rust_prevention_cost ?? 0,
  };
}

/**
 * Reducer function to manage tire state
 * @param state - Current tire state
 * @param action - Action to dispatch
 * @returns Updated tire state
 */
function tireReducer(state: TireState, action: TireAction): TireState {
  switch (action.type) {
    case "SET_TIRE_LIST":
      return { ...state, tireList: action.payload };
    case "APPEND_TIRE_LIST":
      return { ...state, tireList: [...state.tireList, ...action.payload] };
    case "SET_ORIGIN_TIRE_LIST":
      return { ...state, originTireMakerList: action.payload };
    case "APPEND_ORIGIN_TIRE_LIST":
      return { ...state, originTireMakerList: [...state.originTireMakerList, ...action.payload] };
    case "SET_MAKER_LIST":
      return { ...state, makerList: action.payload };
    case "SET_SELECTED_MAKER":
      return { ...state, selectedMaker: action.payload };
    case "SET_CATEGORY_LIST":
      return { ...state, categoryList: action.payload };
    case "SET_SELECTED_CATEGORY":
      return { ...state, selectedCategory: action.payload };
    case "SET_SELECTED_TIRE_IDS":
      return { ...state, selectedTireIds: action.payload };
    case "MERGE_SELECTED_TIRE_IDS":
      // Spreads new page entries on top of existing map: preserves any rows the
      // user already checked on earlier pages (their keys are disjoint).
      return { ...state, selectedTireIds: { ...state.selectedTireIds, ...action.payload } };
    case "UPDATE_SELECTED_TIRE_IDS":
      return {
        ...state,
        selectedTireIds: {
          ...state.selectedTireIds,
          [action.payload.tireId]: action.payload.selected,
        },
      };
    case "RESET_SELECTED_TIRE_IDS":
      return { ...state, selectedTireIds: {}, totalSelectedTire: 0 };
    case "SET_TOTAL_SELECTED_TIRE":
      return { ...state, totalSelectedTire: action.payload };
    case "ADD_TOTAL_SELECTED_TIRE":
      return { ...state, totalSelectedTire: state.totalSelectedTire + action.payload };
    case "INCREMENT_TOTAL_SELECTED_TIRE":
      return { ...state, totalSelectedTire: state.totalSelectedTire + 1 };
    case "SET_PROC_FEES":
      return { ...state, procFees: normalizeProcFees(action.payload) };
    case "UPDATE_PROC_FEES": {
      const updatedProcFees = state.procFees
        ? { ...state.procFees, ...action.payload }
        : ({ ...action.payload } as MstProcFeeTireItem);
      return {
        ...state,
        procFees: normalizeProcFees(updatedProcFees),
      };
    }
    case "SET_PUNCTURE_PROTECTION":
      return { ...state, punctureProtection: action.payload };
    case "SET_TIRES":
      return { ...state, tires: action.payload };
    case "SET_PAGE":
      return { ...state, page: action.payload };
    case "INCREMENT_PAGE":
      return { ...state, page: state.page + 1 };
    case "SET_TOTAL":
      return { ...state, total: action.payload };
    case "SET_RECOMMEND_FLAG":
      return { ...state, recommendFlag: action.payload };
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    case "SET_PRICE_SETTING_WHEEL":
      return { ...state, priceSettingWheel: action.payload };
    case "RESET_FILTER":
      return {
        ...state,
        total: 0,
        page: 1,
        originTireMakerList: [],
      };
    default:
      return state;
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Read data from sessionStorage
 */
function getSessionStorageData(): SessionStorageData {
  return {
    previousPage: sessionStorage.getItem(SESSION_KEY.NEW_QUOTE_FROM) || "",
    tireCategory: sessionStorage.getItem(SESSION_KEY.TIRE_TYPE),
    mstProcFee: JSON.parse(sessionStorage.getItem(SESSION_KEY.SELECTED_TIRE) || "{}"),
    currentPage: Number(sessionStorage.getItem(SESSION_KEY.SET_TIRE_CURRENT_PAGE) || 1),
    selectedMaker: JSON.parse(sessionStorage.getItem(SESSION_KEY.SET_TIRE_SELECTED_MAKER) || "[]"),
    selectedCategory: JSON.parse(
      sessionStorage.getItem(SESSION_KEY.SET_TIRE_SELECTED_CATEGORY) || "[]",
    ),
    storageTire: JSON.parse(sessionStorage.getItem(SESSION_KEY.NEW_QUOTE_TIRE) || "[]"),
    recommendFlag: JSON.parse(
      sessionStorage.getItem(SESSION_KEY.SET_TIRE_RECOMMEND_FLAG) || "false",
    ),
  };
}

/**
 * Format tire list and compute per-page selection state.
 *
 * Key change: selection is now keyed by String(tire.id) — the server-assigned
 * numeric PK — instead of an array index.
 *
 * Why not index?  When page 2 is appended the same index value refers to a
 * different tire, so SET_SELECTED_TIRE_IDS (which replaces the whole map)
 * would overwrite page-1 selections.
 *
 * Why not item_cd?  item_cd may contain duplicates across pages.
 *
 * id is guaranteed unique by the server schema.
 */
function formatTireList(
  list: ITireItemDetail[],
  mstProcFee: any,
  priceSettingWheel: number,
): {
  formattedList: ITireItemDetail[];
  selectedIds: Record<string, boolean>;
  totalSelected: number;
} {
  const selectedIds: Record<string, boolean> = {};
  let totalSelected = 0;

  const formattedList = list
    .filter((tire) => tire.list_price !== null)
    .map((tire) => {
      const matchedTire = mstProcFee?.tbl_quote_detail_tire?.find(
        (item: Partial<EstQuoteDetailTire>) => item.tire_item_cd === tire.item_cd,
      );

      selectedIds[String(tire.id)] = !!matchedTire;

      // response.list.store.price_setting_wheel; attach per-item so the
      // calculator can branch without extra plumbing.
      const withPriceSetting = { ...tire, price_setting_wheel: priceSettingWheel };

      if (matchedTire) {
        totalSelected += 1;
        return { ...withPriceSetting, special_discount: matchedTire.special_discount };
      }

      return withPriceSetting;
    });

  return { formattedList, selectedIds, totalSelected };
}

/**
 * Build request params for API
 * @param storageTire - List of tires from session storage
 * @param previousPage - Previous page identifier
 * @param body - Request body parameters
 * @returns Formatted API parameters
 */
function buildApiParams(
  storageTire: ITireInfo[],
  previousPage: string,
  body: MstItemDetailTireSelect,
): SelectedTireProcFeeParams {
  const params: any = {
    ...storageTire[0],
    ...body,
    [storageTire.length > 1 ? "item_cds" : "item_cd"]:
      storageTire.length > 1
        ? storageTire.map((el) => el.item_cd).join(",")
        : storageTire[0].item_cd,
  };

  // Remove unnecessary fields when coming from product-code page
  if (previousPage === "product-code") {
    delete params.tire_width;
    delete params.tire_loin_speed_mark;
    delete params.tire_aspect_ratio;
    delete params.tire_size;
  }

  if (storageTire.length > 1) {
    delete params.item_cd;
  }

  return params as SelectedTireProcFeeParams;
}

// ============================================================================
// Main Hook
// ============================================================================

/**
 * Custom hook to fetch and manage a paginated list of procedure fees.
 */
const useSetTireProcFeeList = () => {
  const [state, dispatch] = useReducer(tireReducer, initialState);
  const loadMoreRef = useRef(false);
  const sessionDataRef = useRef<SessionStorageData | null>(null);

  // Computed values
  const totalPage = useMemo(() => Math.ceil(state.total / PER_PAGE_DEFAULT), [state.total]);

  // Lazy load session storage data
  const getSessionData = useCallback(() => {
    if (!sessionDataRef.current) {
      sessionDataRef.current = getSessionStorageData();
    }
    return sessionDataRef.current;
  }, []);

  // ============================================================================
  // API Calls
  // ============================================================================

  /**
   * Fetch tire data from API
   */
  const fetchTireData = useCallback(
    async (body: MstItemDetailTireSelect) => {
      const sessionData = getSessionData();
      dispatch({ type: "SET_LOADING", payload: true });

      try {
        const prevScrollY = document.documentElement.scrollTop || document.body.scrollTop;
        const params = buildApiParams(sessionData.storageTire, sessionData.previousPage, body);
        const response = (await getItemDetailTireSetApi(params)) as TireResponse;

        dispatch({ type: "SET_TOTAL", payload: response.total });

        // Response-level price setting flag from list.store.price_setting_wheel.
        const priceSettingWheel = response.list?.store?.price_setting_wheel ?? 0;
        dispatch({ type: "SET_PRICE_SETTING_WHEEL", payload: priceSettingWheel });

        // Reset selected tires when loading first page
        if (body.offset === 1) {
          dispatch({ type: "RESET_SELECTED_TIRE_IDS" });
        }

        // Format tire list — keyed by String(tire.id), not array index or item_cd
        const { formattedList, selectedIds, totalSelected } = formatTireList(
          response.list?.mst_item_detail_tire || [],
          sessionData.mstProcFee,
          priceSettingWheel,
        );

        // Fetch inventory data and map to tire list
        const tireListWithInventory = await fetchAndMapInventory(
          response.list?.mst_item_detail_tire || [],
          formattedList,
        );

        if (body.offset > 1) {
          // Load-more: append tiles and MERGE selection state.
          // Bug fix: SET_SELECTED_TIRE_IDS would replace the whole map, wiping
          // any rows the user checked on earlier pages.  MERGE_SELECTED_TIRE_IDS
          // spreads new entries on top so earlier selections are preserved.
          dispatch({ type: "APPEND_TIRE_LIST", payload: tireListWithInventory });
          dispatch({
            type: "APPEND_ORIGIN_TIRE_LIST",
            payload: response.list?.mst_item_detail_tire || [],
          });
          dispatch({ type: "MERGE_SELECTED_TIRE_IDS", payload: selectedIds });
          dispatch({ type: "ADD_TOTAL_SELECTED_TIRE", payload: totalSelected });
        } else {
          // First page: replace everything (clean slate after filter / initial load).
          dispatch({ type: "SET_TIRE_LIST", payload: tireListWithInventory });
          dispatch({
            type: "SET_ORIGIN_TIRE_LIST",
            payload: response.list?.mst_item_detail_tire || [],
          });

          // Set maker list (only on first load)
          if (state.makerList.length === 0) {
            const makers = response.list.maker_list.map((item) => ({
              label: item.maker_name,
              value: item.maker_name,
              selected: true,
            }));
            const uniqueMakers = uniqBy(makers, "value");

            if (sessionData.selectedMaker.length > 0) {
              dispatch({ type: "SET_MAKER_LIST", payload: sessionData.selectedMaker });
              dispatch({
                type: "SET_SELECTED_MAKER",
                payload: sessionData.selectedMaker.map((item) => item.selected ?? true),
              });
            } else {
              dispatch({ type: "SET_MAKER_LIST", payload: uniqueMakers });
              dispatch({ type: "SET_SELECTED_MAKER", payload: uniqueMakers.map(() => true) });
            }
          }

          // Set category list (only on first load)
          if (state.categoryList.length === 0) {
            const categories =
              response.list?.category_list?.map((el: { category: string }) => ({
                category: el.category,
                selected: false,
              })) || [];

            if (sessionData.selectedCategory.length > 0) {
              dispatch({ type: "SET_CATEGORY_LIST", payload: sessionData.selectedCategory });
              dispatch({
                type: "SET_SELECTED_CATEGORY",
                payload: sessionData.selectedCategory.map((item) => item.selected ?? false),
              });
            } else {
              dispatch({ type: "SET_CATEGORY_LIST", payload: categories });
              dispatch({
                type: "SET_SELECTED_CATEGORY",
                payload: categories.map(() => false),
              });
            }
          }

          dispatch({ type: "SET_SELECTED_TIRE_IDS", payload: selectedIds });
          dispatch({ type: "SET_TOTAL_SELECTED_TIRE", payload: totalSelected });
        }

        // Set proc fees (only once)
        if (!state.procFees) {
          dispatch({ type: "SET_PROC_FEES", payload: response.list.mst_proc_fee });
        }

        // Restore scroll position
        window.scrollTo(0, prevScrollY || 0);
      } catch (error) {
        // Error fetching tire data - can add error handling (toast notification, etc.)
        if (process.env.NODE_ENV === "development") {
          // eslint-disable-next-line no-console
          console.error("Error fetching tire data:", error);
        }
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    },
    [getSessionData, state.makerList.length, state.categoryList.length, state.procFees],
  );

  const { queryParams, applyFilter } = useProcFeeFilter({
    makerList: state.makerList,
    categoryList: state.categoryList,
    selectedMaker: state.selectedMaker,
    selectedCategory: state.selectedCategory,
    setSelectedMaker: (v) => dispatch({ type: "SET_SELECTED_MAKER", payload: v }),
    setSelectedCategory: (v) => dispatch({ type: "SET_SELECTED_CATEGORY", payload: v }),
  });

  /**
   * Fetch puncture protection data
   */
  const fetchPunctureProtection = useCallback(async () => {
    try {
      const response = await getPructureProtectionApi();
      const sortedList = response.list.sort(
        (a: { coverage_amount_low: number }, b: { coverage_amount_low: number }) =>
          a.coverage_amount_low - b.coverage_amount_low,
      );
      dispatch({ type: "SET_PUNCTURE_PROTECTION", payload: sortedList });
    } catch (error) {
      // Error fetching puncture protection
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.error("Error fetching puncture protection:", error);
      }
    }
  }, []);

  // ============================================================================
  // Event Handlers
  // ============================================================================

  /**
   * Apply filter. Modal passes overrides on Apply; same-tick API uses resolved selection.
   */
  const handleFilter = useCallback(
    async (overrides?: ProcFeeFilterSelection) => {
      const resolved = applyFilter(overrides);

      dispatch({ type: "RESET_FILTER" });
      loadMoreRef.current = false;

      const filterParams = buildProcFeeFilterQueryParams(state.makerList, state.categoryList, resolved);

      await fetchTireData({
        offset: 1,
        limit: PER_PAGE_DEFAULT,
        ...filterParams,
      });

      sessionStorage.setItem(
        SESSION_KEY.SET_TIRE_SELECTED_MAKER,
        JSON.stringify(
          state.makerList.map((el, index) => ({
            ...el,
            selected: resolved.selectedMaker[index],
          })),
        ),
      );
      sessionStorage.setItem(
        SESSION_KEY.SET_TIRE_SELECTED_CATEGORY,
        JSON.stringify(
          state.categoryList.map((el, index) => ({
            ...el,
            selected: resolved.selectedCategory[index],
          })),
        ),
      );
    },
    [state.makerList, state.categoryList, applyFilter, fetchTireData],
  );

  /**
   * Load more tires (infinite scroll)
   */
  const onLoadMore = useCallback(async () => {
    if (
      state.page + 1 > totalPage ||
      state.originTireMakerList.length === 0 ||
      state.originTireMakerList.length < PER_PAGE_DEFAULT ||
      loadMoreRef.current ||
      state.loading
    ) {
      return;
    }

    loadMoreRef.current = true;

    try {
      dispatch({ type: "INCREMENT_PAGE" });

      await fetchTireData({
        offset: state.page + 1,
        limit: PER_PAGE_DEFAULT,
        ...queryParams,
      });
    } finally {
      loadMoreRef.current = false;
    }
  }, [
    state.page,
    state.loading,
    totalPage,
    state.originTireMakerList.length,
    queryParams,
    fetchTireData,
  ]);

  // ============================================================================
  // Effects
  // ============================================================================

  /**
   * Initialize puncture protection data
   */
  useEffect(() => {
    fetchPunctureProtection();
  }, [fetchPunctureProtection]);

  /**
   * Sync page to sessionStorage when it changes
   */
  // useEffect(() => {
  //   if (state.page > 0) {
  //     sessionStorage.setItem(SESSION_KEY.SET_TIRE_CURRENT_PAGE, String(state.page));
  //   }
  // }, [state.page]);

  /**
   * Initialize tire data and session storage
   */
  useEffect(() => {
    const sessionData = getSessionData();

    if (sessionData.storageTire.length === 0) return;

    // Reset page to 1 on reload/mount
    // dispatch({ type: "SET_PAGE", payload: 1 });
    // sessionStorage.setItem(SESSION_KEY.SET_TIRE_CURRENT_PAGE, "1");

    // Set initial tire and recommend flag
    dispatch({ type: "SET_TIRES", payload: sessionData.storageTire });
    dispatch({ type: "SET_RECOMMEND_FLAG", payload: sessionData.recommendFlag });

    // Fetch initial data for all previously loaded pages
    const fetchInitialData = async () => {
      const filterParams = buildProcFeeFilterQueryParams(
        sessionData.selectedMaker.length > 0 ? sessionData.selectedMaker : state.makerList,
        sessionData.selectedCategory.length > 0 ? sessionData.selectedCategory : state.categoryList,
        {
          selectedMaker: sessionData.selectedMaker.map((item) => item.selected ?? true),
          selectedCategory: sessionData.selectedCategory.map((item) => item.selected ?? false),
        },
      );

      // Load all previously viewed pages
      const promises = [];
      for (let i = 1; i <= sessionData.currentPage; i += 1) {
        promises.push(
          fetchTireData({
            offset: i,
            limit: PER_PAGE_DEFAULT,
            ...filterParams,
          }),
        );
      }

      await Promise.all(promises);

      // Update proc fees from session storage
      if (sessionData.mstProcFee?.proc_fees) {
        dispatch({
          type: "UPDATE_PROC_FEES",
          payload: {
            replac_labor_cost: sessionData.mstProcFee.proc_fees.replac_labor_cost?.value,
            waste_tire_cost: sessionData.mstProcFee.proc_fees.waste_tire_cost?.value,
            rubber_stem_valve_cost: sessionData.mstProcFee.proc_fees.rubber_stem_valve_cost?.value,
            nitrogen_inflation_cost:
              sessionData.mstProcFee.proc_fees.nitrogen_inflation_cost?.value,
            hub_rust_prevention_cost:
              sessionData.mstProcFee.proc_fees.hub_rust_prevention_cost?.value,
          },
        });
      }
    };

    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Bug fix (duplicate load-more calls): the scroll listener that was here has
  // been removed.  The component uses useLoadMore (IntersectionObserver on a
  // sentinel element) to trigger onLoadMore.  Having both a scroll listener and
  // an IntersectionObserver caused onLoadMore to fire twice per scroll event,
  // so the offset jumped by 2 (e.g. 1 → 3) instead of 1 (1 → 2).
  // useLoadMore already debounces and guards duplicate calls, so one mechanism
  // is sufficient.

  // ============================================================================
  // Return
  // ============================================================================

  return {
    // State
    procFees: state.procFees,
    tires: state.tires,
    tireList: state.tireList,
    makerList: state.makerList,
    categoryList: state.categoryList,
    selectedCategory: state.selectedCategory,
    selectedTireIds: state.selectedTireIds,
    selectedMaker: state.selectedMaker,
    totalSelectedTire: state.totalSelectedTire,
    page: state.page,
    recommendFlag: state.recommendFlag,
    punctureProtection: state.punctureProtection,
    tireCategory: getSessionData().tireCategory,
    loading: state.loading,
    mstProcFee: getSessionData().mstProcFee,
    priceSettingWheel: state.priceSettingWheel,

    // Actions
    setProcFees: (payload: MstProcFeeTireItem) => dispatch({ type: "SET_PROC_FEES", payload }),
    setTires: (payload: ITireInfo[]) => dispatch({ type: "SET_TIRES", payload }),
    setTireList: (payload: ITireItemDetail[]) => dispatch({ type: "SET_TIRE_LIST", payload }),
    setMakerList: (payload: TOption[]) => dispatch({ type: "SET_MAKER_LIST", payload }),
    setSelectedCategory: (payload: boolean[]) =>
      dispatch({ type: "SET_SELECTED_CATEGORY", payload }),
    setSelectedTireIds: (payload: Record<string, boolean>) =>
      dispatch({ type: "SET_SELECTED_TIRE_IDS", payload }),
    setSelectedMaker: (payload: boolean[]) => dispatch({ type: "SET_SELECTED_MAKER", payload }),
    setTotalSelectedTire: (payload: number) =>
      dispatch({ type: "SET_TOTAL_SELECTED_TIRE", payload }),
    setRecommendFlag: (payload: boolean) => dispatch({ type: "SET_RECOMMEND_FLAG", payload }),
    handleFilter,
    onLoadMore,
  };
};

export default useSetTireProcFeeList;
